import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, Megaphone, Search, Users } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { LudoPanel } from "@/components/LudoPanel";
import { DamesPanel } from "@/components/DamesPanel";
import { useQuery } from "@tanstack/react-query";
import { RoomButtons } from "@/components/PrivateRoom";
import { Input } from "@/components/ui/input";
import { usePublicSettings } from "@/components/DepositDialog";
import { useWallet } from "@/components/BalanceChip";
import { api, errCode, formatXAF } from "@/lib/api";
import { useRealtime, useRealtimeEvent } from "@/lib/realtime";
import { useI18n } from "@/i18n/I18nContext";

export const commissionOf = (settings, n, stake) => {
  const pct = settings.commission_by_players?.[String(n)] ?? settings.commission_percent;
  const pot = stake * n;
  const commission = Math.floor((pot * pct) / 100);
  return { pot, pct, commission, gain: pot - commission };
};

const Choice = ({ active, onClick, children, testId, className = "" }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`h-12 rounded-xl border font-semibold transition-all duration-150 active:scale-95 ${active ? "border-amber-400 bg-amber-500/15 text-amber-300" : "border-white/10 bg-white/5 text-slate-300"} ${className}`}>
    {children}
  </button>
);

const LiveQueues = ({ onPick }) => {
  const { t, lang } = useI18n();
  const { counts, online } = useRealtime();
  const rows = Object.entries(counts).map(([k, c]) => {
    const [n, s] = k.split(":").map(Number);
    return { n, s, c };
  }).sort((a, b) => b.c / b.n - a.c / a.n);
  return (
    <section className="surface p-5 sm:p-6" data-testid="lobby-live-queues">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">{t("lobby.waitingNow")}</h2>
        <span className="flex items-center gap-2 text-xs text-slate-400" data-testid="lobby-online-count">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> {t("lobby.online", { n: online })}
        </span>
      </div>
      {rows.length ? (
        <ul className="mt-3 divide-y divide-white/5">
          {rows.map((r) => (
            <li key={`${r.n}:${r.s}`}>
              <button onClick={() => onPick(r.n, r.s)} className="flex min-h-[52px] w-full items-center justify-between text-left" data-testid={`live-queue-${r.n}-${r.s}`}>
                <span className="text-sm">{t("lobby.waitingFmt", { n: r.n, stake: formatXAF(r.s, lang) })}</span>
                <span className="font-mono text-sm font-semibold text-amber-300">{t("queue.counter", { a: r.c, b: r.n })}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-6 text-sm text-slate-500" data-testid="lobby-no-queues">{t("lobby.noneWaiting")}</p>
      )}
    </section>
  );
};

const Summary = ({ settings, n, stake }) => {
  const { t, lang } = useI18n();
  const { counts } = useRealtime();
  const c = commissionOf(settings, n, stake);
  const waiting = counts[`${n}:${stake}`] || 0;
  return (
    <div className="grid grid-cols-3 gap-2 rounded-xl border border-white/10 bg-black/20 p-4 text-center" data-testid="lobby-summary">
      <div><p className="text-[11px] uppercase tracking-wider text-slate-500">{t("lobby.pot")}</p><p className="font-mono text-sm font-semibold" data-testid="lobby-pot">{formatXAF(c.pot, lang)}</p></div>
      <div><p className="text-[11px] uppercase tracking-wider text-slate-500">{t("lobby.commission")} {c.pct}%</p><p className="font-mono text-sm font-semibold text-red-300" data-testid="lobby-commission">{formatXAF(c.commission, lang)}</p></div>
      <div><p className="text-[11px] uppercase tracking-wider text-slate-500">{t("lobby.gain")}</p><p className="font-mono text-sm font-semibold text-emerald-400" data-testid="lobby-gain">{formatXAF(c.gain, lang)}</p></div>
      <p className="col-span-3 pt-2 text-xs text-amber-300" data-testid="lobby-selected-waiting">{t("queue.counter", { a: waiting, b: n })}</p>
    </div>
  );
};

const StakePicker = ({ settings, stake, setStake }) => {
  const { t } = useI18n();
  const [custom, setCustom] = useState("");
  const pickCustom = (v) => {
    setCustom(v);
    const n = parseInt(v, 10);
    if (Number.isInteger(n)) setStake(n);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("lobby.stakeLabel")}</p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {settings.stakes.map((s) => (
          <Choice key={s} active={stake === s && !custom} onClick={() => { setCustom(""); setStake(s); }} testId={`stake-option-${s}`} className="font-mono text-sm">{s}</Choice>
        ))}
      </div>
      {settings.allow_custom_stake && (
        <div className="space-y-1">
          <Input type="number" inputMode="numeric" min={settings.stake_min} max={settings.stake_max} step={1} value={custom}
            placeholder={t("lobby.custom")} onChange={(e) => pickCustom(e.target.value)}
            className="h-12 rounded-xl border-white/10 bg-white/5 font-mono" data-testid="stake-custom-input" />
          <p className="text-xs text-slate-500">{t("lobby.customHint", { min: settings.stake_min, max: settings.stake_max })}</p>
        </div>
      )}
    </div>
  );
};

const TableForm = ({ settings, n, setN, stake, setStake }) => {
  const { t, tErr, lang } = useI18n();
  const navigate = useNavigate();
  const { data: wallet } = useWallet();
  const [busy, setBusy] = useState(false);
  const valid = Number.isInteger(stake) && stake >= settings.stake_min && stake <= settings.stake_max;
  const enough = (wallet?.available ?? 0) >= stake;
  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/queue/join", { player_count: n, stake });
      navigate(data.status === "matched" ? `/game/${data.game_id}` : "/queue");
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="surface space-y-6 p-5 sm:p-6" data-testid="lobby-table-form">
      <div className="space-y-3">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500"><Users className="h-4 w-4" /> {t("lobby.playersLabel")}</p>
        <div className="grid grid-cols-5 gap-2">
          {settings.player_counts.map((c) => (
            <Choice key={c} active={n === c} onClick={() => setN(c)} testId={`player-count-${c}`} className="font-display text-lg">{c}</Choice>
          ))}
        </div>
      </div>
      <StakePicker settings={settings} stake={stake} setStake={setStake} />
      {valid && <Summary settings={settings} n={n} stake={stake} />}
      <p className={`text-sm ${enough ? "text-slate-400" : "text-red-300"}`} data-testid="lobby-balance">
        {t("lobby.balance")} : <span className="font-mono">{formatXAF(wallet?.available ?? 0, lang)}</span>
        {!enough && <> · {t("lobby.insufficient")} <Link to="/wallet" className="font-semibold text-amber-400 underline">{t("lobby.topup")}</Link></>}
      </p>
      <button onClick={search} disabled={busy || !valid || !enough || settings.maintenance} data-testid="lobby-search-btn"
        className="pulse-ring flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display text-base font-bold text-[#0B0D12] transition-transform active:scale-[0.98] disabled:animate-none disabled:opacity-50">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />} {t("lobby.search")}
      </button>
      <RoomButtons n={n} stake={stake} valid={valid && enough && !settings.maintenance} />
    </section>
  );
};

const Announcements = () => {
  const { lang } = useI18n();
  const { data, refetch } = useQuery({ queryKey: ["announcements"], queryFn: async () => (await api.get("/announcements")).data });
  useRealtimeEvent((m) => {
    if (m.type === "announcements_changed") refetch();
  });
  if (!data?.length) return null;
  return (
    <div className="space-y-2" data-testid="lobby-announcements">
      {data.map((a) => (
        <div key={a.id} className={`flex gap-3 rounded-xl border p-4 text-sm ${a.kind === "maintenance" ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-amber-500/25 bg-amber-500/10 text-amber-100"}`}>
          <Megaphone className="mt-0.5 h-4 w-4 shrink-0" /> {(lang === "en" && a.message_en) || a.message_fr}
        </div>
      ))}
    </div>
  );
};

const DemoCard = () => {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["demo-status"], queryFn: () => api.get("/demo/status").then((r) => r.data) });
  if (!data || data.used) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="lobby-demo-card">
      <Gift className="h-6 w-6 shrink-0 text-amber-400" />
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold">{t("demo.cardTitle")}</p>
        <p className="text-sm text-slate-300">{t("demo.cardText")}</p>
      </div>
      <Link to="/demo" className="inline-flex h-11 items-center rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12] transition-colors hover:bg-amber-400" data-testid="lobby-demo-btn">
        {t("demo.cardCta")}
      </Link>
    </div>
  );
};

export default function LobbyPage() {
  const [params, setParams] = useSearchParams();
  const g = params.get("game");
  const gameKind = g === "ludo" || g === "dames" ? g : "dice";
  const { t } = useI18n();
  return (
    <div className="space-y-5">
      <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1" data-testid="lobby-game-switch">
        {["dice", "ludo", "dames"].map((k) => (
          <button key={k} type="button" onClick={() => setParams(k === "dice" ? {} : { game: k })} data-testid={`lobby-game-${k}`}
            className={`h-10 rounded-full px-5 text-sm font-bold transition-colors ${gameKind === k ? "bg-amber-500 text-[#0B0D12]" : "text-slate-300"}`}>{t(`board.${k}`)}</button>
        ))}
      </div>
      {gameKind === "ludo" ? <LudoPanel /> : gameKind === "dames" ? <DamesPanel /> : <DiceLobby />}
    </div>
  );
}

function DiceLobby() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: settings } = usePublicSettings();
  const [n, setN] = useState(3);
  const [stake, setStake] = useState(25);

  useEffect(() => {
    api.get("/play/active").then(({ data }) => {
      if (data.kind === "game") navigate(`/game/${data.game_id}`);
      if (data.kind === "queue") navigate("/queue");
    }).catch(() => {});
  }, [navigate]);

  if (!settings) return null;
  return (
    <div className="space-y-5 rise" data-testid="lobby-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("lobby.title2")}</h1>
      <Announcements />
      <DemoCard />
      <Link to="/des/essai" className="block text-sm text-amber-300 underline" data-testid="lobby-dice-practice-btn">{t("demo.practice")}</Link>
      {settings.maintenance && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{t("errors.maintenance")}</div>}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <TableForm settings={settings} n={n} setN={setN} stake={stake} setStake={setStake} />
        <LiveQueues onPick={(pc, s) => { setN(pc); setStake(s); }} />
      </div>
    </div>
  );
}
